import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_TOKEN } from '@aksia/infrastructure';
import { ToasterService } from '@aksia/ui';
import { HttpErrorResponse } from '@angular/common/http';
import { isValidReturnUrl } from '../../utils/return-url.utils';
import {
  SECOND_FACTOR_LABELS,
  SecondFactorMethod,
  SecondFactorService,
} from '../../services/second-factor.service';

const KNOWN_METHODS: SecondFactorMethod[] = [
  'Email',
  'Sms',
  'Authenticator',
  'RecoveryCode',
];

@Component({
  selector: 'app-verify-location',
  imports: [],
  templateUrl: './verify-location.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyLocationPageContent {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly toaster = inject(ToasterService);
  private readonly secondFactor = inject(SecondFactorService);
  protected readonly currentYear = new Date().getFullYear();
  protected readonly labels = SECOND_FACTOR_LABELS;

  protected readonly code = signal('');
  protected readonly rememberDevice = signal(false);
  protected readonly inFlight = signal(false);
  protected readonly sending = signal(false);
  protected readonly attemptedSubmit = signal(false);
  protected readonly bounced = signal(false);
  protected readonly formShaking = signal(false);
  protected readonly suggestAuthenticator = signal(false);

  private readonly queryParams = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap,
  });

  protected readonly returnUrl = computed(
    () => this.queryParams().get('returnUrl') ?? undefined,
  );

  protected readonly returnUrlValid = computed(() =>
    isValidReturnUrl(this.returnUrl()),
  );

  protected readonly methods = computed<SecondFactorMethod[]>(() => {
    const listed = (this.queryParams().get('methods') ?? '')
      .split(',')
      .filter((m): m is SecondFactorMethod =>
        KNOWN_METHODS.includes(m as SecondFactorMethod),
      );
    return listed.length ? listed : ['Email'];
  });

  protected readonly method = signal<SecondFactorMethod>(
    (this.route.snapshot.queryParamMap.get('method') as SecondFactorMethod) ??
      'Email',
  );

  protected readonly maskedTarget = signal<string | null>(
    this.route.snapshot.queryParamMap.get('to'),
  );

  private readonly sentFor = signal<Set<SecondFactorMethod>>(
    new Set([this.method()]),
  );

  protected readonly canSendCode = computed(
    () => this.method() === 'Email' || this.method() === 'Sms',
  );

  protected readonly codeMaxLength = computed(() =>
    this.method() === 'RecoveryCode'
      ? 11
      : this.method() === 'Authenticator'
        ? 6
        : 8,
  );

  protected readonly instructions = computed(() => {
    const to = this.maskedTarget();
    switch (this.method()) {
      case 'Authenticator':
        return 'Enter the 6-digit code from your authenticator app.';
      case 'RecoveryCode':
        return 'Enter one of your recovery codes, e.g. ABCDE-12345. Each code works once.';
      case 'Sms':
        return to
          ? `Enter the code we texted to ${to}.`
          : 'Enter the code we texted to your phone.';
      default:
        return to
          ? `Enter the code sent to ${to}.`
          : 'Enter the code sent to your email address.';
    }
  });

  @HostBinding('class')
  protected readonly hostClass = 'auth-page';

  protected updateCode(value: string) {
    const cleaned =
      this.method() === 'RecoveryCode'
        ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
        : value.replace(/\D/g, '');
    this.code.set(cleaned.slice(0, this.codeMaxLength()));
  }

  protected showCodeError() {
    return this.attemptedSubmit() && !this.code().trim();
  }

  private triggerShake() {
    this.formShaking.set(true);
    setTimeout(() => this.formShaking.set(false), 400);
  }

  protected selectMethod(method: SecondFactorMethod) {
    if (method === this.method()) return;

    this.method.set(method);
    this.code.set('');
    this.attemptedSubmit.set(false);

    if (
      (method === 'Email' || method === 'Sms') &&
      !this.sentFor().has(method)
    ) {
      this.sendCode();
    }
  }

  protected sendCode() {
    const method = this.method();
    this.sending.set(true);

    this.secondFactor.send(method).subscribe({
      next: (res) => {
        this.sending.set(false);
        this.maskedTarget.set(res.maskedTarget ?? null);
        this.sentFor.update((sent) => new Set(sent).add(method));
        this.toaster.success('A new code is on its way.');
      },
      error: (err: HttpErrorResponse) => {
        this.sending.set(false);
        this.handleError(err);
      },
    });
  }

  protected submit(event?: Event) {
    event?.preventDefault();
    this.attemptedSubmit.set(true);

    if (!this.code().trim()) {
      this.triggerShake();
      return;
    }

    if (this.inFlight()) {
      return;
    }

    this.inFlight.set(true);

    this.secondFactor
      .verify(this.method(), this.code().trim(), this.rememberDevice())
      .subscribe({
        next: (res) => {
          if (res.suggestAuthenticator) {
            this.inFlight.set(false);
            this.suggestAuthenticator.set(true);
            return;
          }
          // Keep inFlight set: assigning location.href only schedules the
          // navigation, so the button must stay disabled until the document
          // is torn down, otherwise the code can be submitted twice.
          this.continueToApp();
        },
        error: (err: HttpErrorResponse) => {
          this.inFlight.set(false);
          this.handleError(err);
        },
      });
  }

  protected continueToApp() {
    window.location.href =
      this.environment.microservices_api_url + this.returnUrl();
  }

  protected setUpAuthenticator() {
    this.router.navigate(['/security'], {
      queryParams: { returnUrl: this.returnUrl() },
    });
  }

  private handleError(err: HttpErrorResponse) {
    if (err.status === 429) {
      this.toaster.error(
        'Too many attempts. Please wait a moment and try again.',
      );
      return;
    }

    if (err.status === 401) {
      this.goToLogin();
      return;
    }

    const errorCode = err.error?.error as string | undefined;
    const description = err.error?.error_description as string | undefined;
    const correlationId = err.error?.correlation_id as string | undefined;

    switch (errorCode) {
      case 'code_incorrect':
        this.toaster.error(
          description ??
            'The verification code could not be confirmed. Please try again.',
        );
        break;
      case 'code_expired':
        this.toaster.error(
          description ?? 'Your code has expired. Please ask for a new one.',
        );
        break;
      case 'code_blocked':
        this.toaster.error(
          description ?? 'Too many failed attempts. Please sign in again.',
        );
        this.bounced.set(true);
        break;
      case 'invalid_request':
        this.toaster.error(
          description ?? 'This verification method is not available.',
        );
        break;
      default:
        this.toaster.error(
          correlationId
            ? `Something went wrong. Please try again. Reference: ${correlationId}`
            : 'Something went wrong. Please try again.',
        );
    }
  }

  protected goToLogin() {
    const returnUrl = this.returnUrl();
    this.router.navigate(
      ['/'],
      returnUrl ? { queryParams: { returnUrl } } : {},
    );
  }
}
